import { useQuery } from "@tanstack/react-query";
import { adminApi, currency, categoryMeta } from "@/lib/api";
import { DollarSign, ShoppingCart, Users, UserPlus } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { StatusBadge } from "@/pages/Reservations";

const STAT_META = [
  { key: "total_revenue", label: "Total Revenue", icon: DollarSign, fmt: currency },
  { key: "total_orders", label: "Total Orders", icon: ShoppingCart, fmt: (v) => v },
  { key: "total_customers", label: "Customers", icon: Users, fmt: (v) => v },
  { key: "new_customers_7d", label: "New (7 days)", icon: UserPlus, fmt: (v) => v },
];

export default function AdminDashboard() {
  const { data } = useQuery({ queryKey: ["analytics"], queryFn: async () => (await adminApi.get("/admin/analytics")).data });
  if (!data) return <p className="text-muted2">Loading analytics…</p>;

  const statuses = Object.entries(data.orders_by_status || {});
  const catData = (data.category_performance || []).map((c) => ({ name: categoryMeta[c.category]?.label || c.category, count: c.count }));

  return (
    <div className="space-y-8">
      <div>
        <p className="overline-label mb-1">Overview</p>
        <h1 className="font-serif text-3xl text-cream">Dashboard</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {STAT_META.map((s) => (
          <div key={s.key} data-testid={`admin-stat-${s.key}`} className="gold-card !border-gold/25 p-5">
            <s.icon className="w-5 h-5 text-gold mb-3" />
            <p className="text-2xl font-serif text-cream">{s.fmt(data[s.key])}</p>
            <p className="text-xs text-muted2 mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 gold-card !border-gold/25 p-6">
          <h2 className="font-serif text-xl text-cream mb-5">Orders by Status</h2>
          <div className="space-y-3">
            {statuses.map(([status, count]) => (
              <div key={status} className="flex items-center justify-between">
                <StatusBadge status={status} />
                <span className="text-cream font-mono">{count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-3 gold-card !border-gold/25 p-6">
          <h2 className="font-serif text-xl text-cream mb-5">Category Performance</h2>
          {catData.length === 0 ? (
            <p className="text-muted2 text-sm">No order data yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={catData}>
                <XAxis dataKey="name" tick={{ fill: "#8A8F9A", fontSize: 11 }} interval={0} angle={-25} textAnchor="end" height={60} />
                <YAxis tick={{ fill: "#8A8F9A", fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#14171F", border: "1px solid rgba(201,169,97,0.4)", borderRadius: 12, color: "#F3F4F6" }} cursor={{ fill: "rgba(201,169,97,0.08)" }} />
                <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                  {catData.map((_, i) => <Cell key={i} fill="#C9A961" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="gold-card !border-gold/25 p-6">
        <h2 className="font-serif text-xl text-cream mb-5">Popular Products</h2>
        {(data.popular_products || []).length === 0 ? (
          <p className="text-muted2 text-sm">No orders yet.</p>
        ) : (
          <div className="space-y-3">
            {data.popular_products.map((p, i) => (
              <div key={i} className="flex items-center justify-between border-b border-gold/10 pb-2 last:border-0">
                <span className="text-cream/90"><span className="text-gold mr-3">{i + 1}.</span>{p.name}</span>
                <span className="text-muted2 font-mono text-sm">{p.count} rented</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
